import { QuestionForMain } from "@/components/features/_layouts/island/main/question";
import type { SiteControllerSettingBaseFormModel } from "@/components/features/settings/site-controller/model/base/type";
import type { SiteControllerSettingBeds24FormModel } from "@/components/features/settings/site-controller/model/beds24/type";
import type { SiteControllerSettingBeds24FormModelMethodsType } from "@/components/features/settings/site-controller/model/beds24/use-case/use-create-model/methods";

import { Button } from "@/components/ui/button/button";
import { SelectField } from "@/components/ui/form/fields/select-field";

import { useBeds24RoomTypeMapping } from "./hooks";

type Props = {
	beds24RoomTypeMapping: SiteControllerSettingBeds24FormModel["ViewModel"]["beds24RoomTypeMapping"]; //
	onSetBeds24RoomTypeMapping: SiteControllerSettingBeds24FormModelMethodsType["siteControllerSettingBeds24FormModelMethods"]["onSetBeds24RoomTypeMapping"];

	canSyncMasterData: SiteControllerSettingBeds24FormModelMethodsType["siteControllerSettingBeds24FormModelMethods"]["canSyncMasterData"];
	onSyncMasterData: SiteControllerSettingBeds24FormModelMethodsType["siteControllerSettingBeds24FormModelMethods"]["onSyncMasterData"];

	roomKVList: SiteControllerSettingBaseFormModel["QueryData"]["roomKVList"];
};

export const SettingsSiteControllerBeds24RoomTypeMapping = ({
	beds24RoomTypeMapping,
	onSetBeds24RoomTypeMapping: _onSetBeds24RoomTypeMapping,

	canSyncMasterData: _canSyncMasterData,
	onSyncMasterData: _onSyncMasterData,

	roomKVList,
}: Props) => {
	const { roomTypeMappingOptions, onSetBeds24RoomTypeMapping, canSyncMasterData, onSyncMasterData } =
		useBeds24RoomTypeMapping({
			beds24RoomTypeMapping,
			onSetBeds24RoomTypeMapping: _onSetBeds24RoomTypeMapping,
			roomKVList,
			canSyncMasterData: _canSyncMasterData,
			onSyncMasterData: _onSyncMasterData,
		});

	const guide = `beds24に登録されている部屋情報にCHILLNNの部屋を紐づけてください。<br />
	紐付けが不要な部屋に関しては、「紐付けなし」を選択してください。<br />
	CHILLNNに登録されている部屋を、Beds24上の複数の部屋に紐づけることはできません。`;

	return (
		<QuestionForMain
			guide={guide}
			required={true}
			title={"Beds24の部屋とCHILLNNの部屋の紐付け"}
			inputChildren={
				<div className="space-y-6">
					{beds24RoomTypeMapping.value.length === 0 ? (
						<div>
							<p>表示できるBeds24の部屋がありません</p>
							<p>
								※既にBeds24で部屋を作成している場合は「Beds24の部屋情報を取得する」ボタンを押し、Beds24の部屋情報を新しく取得してください
							</p>
						</div>
					) : (
						<div className="space-y-8">
							{beds24RoomTypeMapping.value.map((roomType) => {
								return (
									<div key={roomType.beds24roomID.value}>
										<SelectField
											label={roomType.name.value}
											value={roomType.roomID?.value ?? "null"}
											options={roomTypeMappingOptions(roomType.roomID?.value ?? undefined)}
											handleValueChange={(input) =>
												onSetBeds24RoomTypeMapping({
													roomID: input,
													beds24RoomID: roomType.beds24roomID.value,
												})
											}
										/>
									</div>
								);
							})}
						</div>
					)}

					<Button
						onClick={onSyncMasterData}
						type="button"
						variant={canSyncMasterData ? "outline" : "disabled"}
					>
						Beds24の部屋情報を取得する
					</Button>
				</div>
			}
		/>
	);
};

SettingsSiteControllerBeds24RoomTypeMapping.displayName = "SettingsSiteControllerBeds24RoomTypeMapping";
